/**
 * 단계별 import UI 의 stage 메타데이터 + 서버 state 타입.
 *
 * 서버의 `StageName` / `STAGE_ORDER` / `StepwiseState` 를 그대로 미러링한다.
 * (admin 은 @fiket/shared 를 쓰지 않고, 서버 타입 직접 import 도 불가하므로 중복 정의.)
 */

export type StageName =
  | 'extract'
  | 'upload'
  | 'scan'
  | 'adapt'
  | 'normalize'
  | 'substitute'
  | 'llm'
  | 'autofix'
  | 'validate'
  | 'inject'

export const STAGE_ORDER: readonly StageName[] = [
  'extract',
  'upload',
  'scan',
  'adapt',
  'normalize',
  'substitute',
  'llm',
  'autofix',
  'validate',
  'inject',
] as const

export type StageStatus = 'idle' | 'running' | 'done' | 'error' | 'invalidated'

export interface StageMeta {
  name: StageName
  label: string
  description: string
}

export const STAGE_META: Record<StageName, StageMeta> = {
  extract: { name: 'extract', label: 'Extract', description: 'ZIP 을 읽고 HTML·CSS·JS·에셋으로 분리' },
  upload: { name: 'upload', label: 'Upload', description: '에셋을 S3 에 업로드하고 HTML 내 경로를 리라이트' },
  scan: { name: 'scan', label: 'Scan', description: 'RSVP/방명록/갤러리 등 필수 앵커 존재 확인' },
  adapt: { name: 'adapt', label: 'Adapt', description: 'id → data-* 속성 치환, placeholder 스크립트 제거' },
  normalize: { name: 'normalize', label: 'Normalize', description: 'id/class 폴백을 data-fw-* 속성으로 통일, 충돌 IIFE 제거' },
  substitute: { name: 'substitute', label: 'Substitute', description: 'data-fiket 마커 → Liquid + data-bind 속성으로 치환' },
  llm: { name: 'llm', label: 'LLM Bind', description: '마커 미지정 영역을 좁은 LLM 패스로 Liquid 바인딩 (skipLlm 시 건너뜀)' },
  autofix: { name: 'autofix', label: 'Autofix', description: 'Liquid 계약 위반을 자동 보정' },
  validate: { name: 'validate', label: 'Validate', description: 'Liquid 문법·컨트롤러 계약 검증' },
  inject: { name: 'inject', label: 'Inject', description: '14종 표준 컨트롤러 스크립트를 HTML 에 주입' },
}

// ─────────────────────────────────────────────────────
// 서버 응답과 1:1 매칭되는 state/stage-log 타입
// ─────────────────────────────────────────────────────

export interface PipelineStageLog {
  stage: StageName
  ok: boolean
  info?: Record<string, unknown>
}

export interface PipelineArtifacts {
  sharedHtml: string
  sharedCss: string | null
  sharedJs: string | null
  cachedHtml: string
  cachedCss: string | null
  uploadedAssets: Array<{
    originalPath: string
    s3Key: string
    s3Url: string
    category: 'font' | 'image' | 'video' | 'css' | 'other'
  }>
}

export interface PipelineLlmUsage {
  model: string
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheCreationTokens: number
}

/** 서버의 `AnchorScanReport` 와 동일. 필요한 필드만 느슨히 정의. */
export interface AnchorScanReport {
  rsvp: Record<string, unknown>
  guestbook: Record<string, unknown>
  gallery: Record<string, unknown>
  fallbacks: Record<string, Record<string, unknown>>
  errors: Array<{ code: string; message: string; hint?: string }>
}

export interface ValidationResult {
  errors: Array<{ code: string; message: string }>
  warnings: Array<{ code: string; message: string }>
}

export interface StepwiseState {
  html: string
  stages: PipelineStageLog[]
  errors: Array<{ stage: string; code?: string; message: string }>
  artifacts?: PipelineArtifacts
  anchors?: AnchorScanReport
  llmUsage?: PipelineLlmUsage
  autofix: { applied: string[]; skipped: string[] }
  validation: ValidationResult
  injected?: string[]
}

export function emptyStepwiseState(): StepwiseState {
  return {
    html: '',
    stages: [],
    errors: [],
    autofix: { applied: [], skipped: [] },
    validation: { errors: [], warnings: [] },
  }
}

/** 지정 stage 이전의 stage (없으면 null). */
export function prevStage(stage: StageName): StageName | null {
  const idx = STAGE_ORDER.indexOf(stage)
  return idx <= 0 ? null : STAGE_ORDER[idx - 1]
}

/** 지정 stage 다음의 stage (없으면 null — inject 이후). */
export function nextStage(stage: StageName): StageName | null {
  const idx = STAGE_ORDER.indexOf(stage)
  return idx < 0 || idx >= STAGE_ORDER.length - 1 ? null : STAGE_ORDER[idx + 1]
}
